import * as Switch from '@radix-ui/react-switch'
import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ConsentCategory } from '@/consent/types'

interface CategoryToggleProps {
  category: ConsentCategory
  checked: boolean
  disabled?: boolean
  onChange?(checked: boolean): void
}

/**
 * CategoryToggle — single cookie category row with name, description and a Radix Switch.
 *
 * Used on /account/privacy/cookies. The "essential" category is always checked
 * and disabled — invariant of the consent model.
 *
 * Visual pattern (best-practice — Material Design 3, iOS/Тинькофф):
 *  - OFF: светлая дорожка + белый thumb (видим контур)
 *  - ON:  залитая accent-brand дорожка + thumb с checkmark (явный visual cue)
 *  - Размер: 52×28 (track) / 22×22 (thumb) — touch-target ≥44px (WCAG 2.5.5)
 */
export function CategoryToggle({ category, checked, disabled, onChange }: CategoryToggleProps) {
  const { t } = useTranslation('consent')
  return (
    <div className="rounded-lg border border-border bg-surface-raised p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-medium text-text-primary">{t(`categories.${category}.name`)}</h3>
          <p className="mt-1 text-sm text-text-secondary">
            {t(`categories.${category}.description`)}
          </p>
        </div>
        <Switch.Root
          checked={checked}
          disabled={disabled}
          onCheckedChange={onChange}
          aria-label={t(`categories.${category}.name`)}
          className="
            relative inline-flex h-7 w-[52px] shrink-0 cursor-pointer items-center
            rounded-full border-2 border-border
            bg-surface-canvas
            transition-colors duration-200
            data-[state=checked]:bg-action-primary-bg
            data-[state=checked]:border-action-primary-bg
            data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-raised
          "
        >
          <Switch.Thumb
            className="
              pointer-events-none flex h-[22px] w-[22px] items-center justify-center
              rounded-full bg-white shadow-md
              transition-transform duration-200
              data-[state=checked]:translate-x-[24px]
              data-[state=unchecked]:translate-x-[1px]
            "
          >
            {checked && (
              <Check
                className="h-3 w-3 text-action-primary-bg"
                strokeWidth={3}
                aria-hidden
              />
            )}
          </Switch.Thumb>
        </Switch.Root>
      </div>
    </div>
  )
}
